// Topologie-Graph für die Topologie-Section + Dashboard-Karte.
//
// Hält den {nodes, edges}-Graphen von GET /v2-api/topology (read-only, aus
// cluster.Monitor abgeleitet). Seit 02.11.2026 ohne eigenen Abruf und Takt: die
// Knoten kommen als Instanzen topologie.<id>.knoten, die Kanten als
// topologie.kanten über das Homelab-Protokoll (docs/protokoll.md › „Netzwerk
// über das Protokoll“) aus dem gemeinsamen Netz-Stand (netz.svelte.ts); REST nur
// beim Start und nach Aktionen (refreshTopology). Erstes Paint sofort aus der
// Ablage dieses Stands.
//
// Zwei Konsumenten:
//   - pages/Topologie.svelte  → voller Graph (Cytoscape)
//   - Dashboard-Karte         → getTopologySummary() (online/total)

import { isEntityDisabled } from "./entity-disables.svelte";
import { getNetzGraph, refreshNetz, startNetz } from "./netz.svelte";

export interface TopoNode {
  id: string;
  label: string;
  kind: string; // external | router | cluster | host
  parent?: string;
  cluster?: string;
  role?: string;
  ip?: string;
  os?: string;
  status: string; // online | warn | offline | unknown
  // Aggregierter Verbindungs-Zustand der Host-Interfaces (Topologie-
  // Schicht "Verbindungen"). Fehlt, wenn nie ein link-event kam.
  link?: TopoLinkInfo;
  // Uptime-Prüfungen, deren Ziel dieser Knoten ist (Schicht "URL-Checks").
  // Fehlt, wenn kein Ziel auf den Knoten zeigt.
  checks?: TopoNodeCheck[];
}

export interface TopoNodeCheck {
  vantage: "internal" | "external" | string;
  health: "up" | "down" | "unknown" | string;
  http_status?: number;
  latency_ms?: number;
  url?: string;
  note?: string; // externer Vantage-Hop, z.B. "via homeserver2-sc"
  last_probe?: string;
}

export interface TopoCheckEdge {
  id: string;
  source: string;
  target: string;
  vantage: "internal" | "external" | string;
  health: "up" | "down" | "unknown" | string;
  label?: string;
}

export interface TopoLinkInfo {
  health: string; // up | degraded | down | unknown
  iface?: string;
  flaps: number;
  last_flap?: string;
  outage: boolean;
}

export interface TopoEdge {
  id: string;
  source: string;
  target: string;
  kind: string; // wan | lan | tailscale | dns | chain
  label?: string;
}

export interface TopoGraph {
  nodes: TopoNode[];
  edges: TopoEdge[];
  check_edges?: TopoCheckEdge[];
}

export interface TopoSummary {
  online: number;
  warn: number;
  offline: number;
  unknown: number;
  total: number;
}

let _off: (() => void) | undefined;
let _letzte: TopoSummary | undefined;

export function startTopologySummary(): void {
  if (_off) return;
  _off = startNetz();
}

export function stopTopologySummary(): void {
  _off?.();
  _off = undefined;
}

export function getTopologyGraph(): TopoGraph | undefined {
  return getNetzGraph();
}

/** Holt den Graphen sofort per REST -- nach einer Aktion, deren Ergebnis die
 *  Seite gleich zeigen soll (das Protokoll braucht einen Moment). */
export function refreshTopology(): Promise<void> {
  return refreshNetz(["graph"]);
}

// getTopologySummary aggregiert die Host-/Router-Zustände für die
// Dashboard-Karte. Cluster- und External-Knoten zählen nicht mit. Hosts in
// einem bewusst deaktivierten Cluster (n.parent = cluster-id) fallen
// ebenfalls raus -- kein falscher "X DOWN" für stillgelegte Inseln.
const _summary = $derived.by((): TopoSummary | undefined => {
  const g = getNetzGraph();
  if (!g) return undefined;
  const s: TopoSummary = { online: 0, warn: 0, offline: 0, unknown: 0, total: 0 };
  for (const n of g.nodes) {
    if (n.kind !== "host" && n.kind !== "router") continue;
    if (n.parent && isEntityDisabled("cluster", n.parent)) continue;
    s.total++;
    if (n.status === "online") s.online++;
    else if (n.status === "warn") s.warn++;
    else if (n.status === "offline") s.offline++;
    else s.unknown++;
  }
  // Dasselbe Objekt, solange sich keine Zahl ändert (ein Stand, der z. B. nur
  // die Prüfungen eines Knotens ändert, löst sonst jede Karte neu aus).
  const l = _letzte;
  if (l && l.online === s.online && l.warn === s.warn && l.offline === s.offline && l.unknown === s.unknown && l.total === s.total) return l;
  _letzte = s;
  return s;
});

export function getTopologySummary(): TopoSummary | undefined {
  return _summary;
}
